/* Shop My Store — PLP/Search pickup-availability toggle.
 *
 * Renders into the theme-owned `#wss-shop-my-store` placeholder on collection/search
 * pages (see sections/searchanise-collection-grid.liquid, sections/main-search.liquid).
 * Reads/writes this widget's own "Shop My Store" state through window.WSSShopMyStore and
 * the selected store through window.WSSStore (assets/store-locator.js) — both must load
 * before this file. WSSShopMyStore is distinct from window.WSSPickupMode (the cart's
 * "Pick Up In Store" toggle) — per the approved BOPIS split, turning this widget on also
 * turns pickup mode on, but not the other way around; see WSSShopMyStore's own comment in
 * store-locator.js for the full asymmetric-link rules.
 *
 * Desktop renders a card widget relocated into Nosto's filter sidebar (see the
 * "Desktop sidebar placement" section below). Mobile renders two things: a CTA above
 * the grid that's always visible, plus a fuller panel injected into Nosto's mobile
 * filter flyout (the same `.ns-sidebar-content` Nosto reuses as the mobile drawer) —
 * see "Mobile filter-flyout panel" below.
 */
(function () {
  'use strict';

  function getConfig() {
    return window.WSS_SHOP_MY_STORE_CONFIG || { facetParam: 'filter.extra.custom-pickup_store_ids' };
  }

  function getContainers() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-wss-shop-my-store]'));
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function iconSvg(extraClass) {
    return '<svg class="wss-shop-my-store__icon ' + (extraClass || '') + '" viewBox="0 0 29 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" d="M29 8.842c0-.267-.072-.53-.207-.759L24.865 1.43A2.94 2.94 0 0 0 23.806.384 2.9 2.9 0 0 0 22.379 0H6.621a2.9 2.9 0 0 0-2.486 1.431L.207 8.083c-.135.23-.207.492-.207.76 0 1.48.56 2.82 1.45 3.857v13.826c0 .391.153.766.425 1.042.272.277.64.432 1.025.432h11.6c.385 0 .753-.155 1.025-.432.272-.276.425-.65.425-1.042v-7.368h5.8v7.368c0 .391.153.766.425 1.042.272.277.64.432 1.025.432h2.9c.385 0 .753-.155 1.025-.432.272-.276.425-.65.425-1.042V12.7c.89-1.037 1.45-2.377 1.45-3.858m-2.923.37a2.96 2.96 0 0 1-.96 1.841 2.87 2.87 0 0 1-1.917.736c-1.6 0-2.9-1.321-2.9-2.947 0-.1-.036-.189-.056-.283l.029-.006-1.104-5.606h3.21zM11.61 8.938l1.178-5.99h3.423l1.179 5.99c-.05 1.581-1.324 2.851-2.89 2.851s-2.84-1.27-2.89-2.851m-4.99-5.99h3.211L8.729 8.552l.029.006c-.022.094-.058.183-.058.283 0 1.626-1.3 2.948-2.9 2.948a2.87 2.87 0 0 1-1.917-.737 2.96 2.96 0 0 1-.96-1.841zm4.98 17.684H5.8V16.21h5.8z"/>' +
      '</svg>';
  }

  // "disabled" here means "no store selected yet", not "unclickable" — per UAT feedback,
  // clicking it in that state should open the store locator, so this stays a real,
  // clickable button. aria-disabled (not the native disabled attribute) keeps it out of
  // the tab order semantics as a non-functioning switch while leaving click/keyboard
  // activation intact; see the dimmed-track style keyed off aria-disabled in the CSS.
  function toggleSwitchHtml(on, disabled) {
    return '<button type="button" class="cart-pickup-toggle wss-shop-my-store__toggle' + (on ? ' cart-pickup-toggle--on' : '') + '"' +
      ' role="switch" aria-checked="' + (on ? 'true' : 'false') + '" aria-label="Shop My Store" data-wss-shop-my-store-toggle' +
      (disabled ? ' aria-disabled="true"' : '') + '>' +
      '<span class="cart-pickup-toggle__track" aria-hidden="true"><span class="cart-pickup-toggle__knob"></span></span>' +
      '</button>';
  }

  // Flip the switch in place (same pattern as renderCartPickupToggles in store-locator.js).
  // Full innerHTML rebuilds destroy the knob mid-transition, so callers must prefer this
  // whenever the button node can be kept.
  function setToggleVisual(toggle, on, disabled) {
    if (!toggle) return;
    var checked = on ? 'true' : 'false';
    if (toggle.getAttribute('aria-checked') !== checked) toggle.setAttribute('aria-checked', checked);
    if (toggle.classList.contains('cart-pickup-toggle--on') !== !!on) {
      toggle.classList.toggle('cart-pickup-toggle--on', !!on);
    }
    if (disabled) {
      if (toggle.getAttribute('aria-disabled') !== 'true') toggle.setAttribute('aria-disabled', 'true');
    } else if (toggle.hasAttribute('aria-disabled')) toggle.removeAttribute('aria-disabled');
  }

  function todaysHours(store) {
    if (!window.WSSStore || typeof window.WSSStore.todaysHoursLabel !== 'function') return null;
    return window.WSSStore.todaysHoursLabel(store) || null;
  }

  function storeAddressLabel(store) {
    if (!store) return '';
    var parts = [store.address1 || '', (store.city || '') + (store.state ? ', ' + store.state : '') + (store.zip ? ' ' + store.zip : '')];
    return parts.filter(Boolean).join(', ');
  }

  function cardBodyHtml(state, store) {
    if (state === 'on') {
      var name = escapeHtml(store.displayName || store.name || '');
      var address = escapeHtml(storeAddressLabel(store));
      var hours = todaysHours(store);
      var hoursHtml = hours
        ? '<p class="wss-shop-my-store__store-hours">' +
            '<span class="wss-shop-my-store__store-hours-day">' + escapeHtml(hours.day) + '</span>' +
            '<span class="wss-shop-my-store__store-hours-time">' + escapeHtml(hours.time) + '</span>' +
          '</p>'
        : '';
      return (
        '<p class="wss-shop-my-store__store-name">Shopping at <strong>' + name + '</strong></p>' +
        (address ? '<p class="wss-shop-my-store__store-address">' + address + '</p>' : '') +
        hoursHtml +
        '<button type="button" class="wss-shop-my-store__change-store" data-wss-shop-my-store-change>Change Store</button>'
      );
    }
    if (state === 'disabled') {
      return (
        '<p class="wss-shop-my-store__desc">Find a store to view product availability</p>' +
        '<button type="button" class="wss-shop-my-store__select-store" data-wss-shop-my-store-select>Select a Store</button>'
      );
    }
    return '<p class="wss-shop-my-store__desc">View product availability at your store</p>';
  }

  // Simplified body for the mobile filter-flyout panel — per the approved use cases this
  // shows just "Shopping at [Location]" + "Change Store" (no address/hours; that detail
  // stays desktop-only).
  function filterPanelBodyHtml(state, store) {
    if (state === 'on') {
      var name = escapeHtml(store.displayName || store.name || '');
      return (
        '<p class="wss-shop-my-store__store-name">Shopping at <strong>' + name + '</strong></p>' +
        '<button type="button" class="wss-shop-my-store__change-store" data-wss-shop-my-store-change>Change Store</button>'
      );
    }
    if (state === 'disabled') {
      return (
        '<p class="wss-shop-my-store__desc">Find a store to view product availability</p>' +
        '<button type="button" class="wss-shop-my-store__select-store" data-wss-shop-my-store-select>Select a Store</button>'
      );
    }
    return '<p class="wss-shop-my-store__desc">View product availability at your store</p>';
  }

  // Shared click wiring for the toggle/Change Store/Select a Store controls, used by
  // both the primary container's desktop card and the mobile filter-flyout panel.
  // Handlers read live computeState() (not a render-time closure) so in-place updates
  // that keep the same button node don't leave stale on/off/disabled logic behind.
  function bindStateControls(root, source) {
    var toggle = root.querySelector('[data-wss-shop-my-store-toggle]');
    if (toggle && !toggle.__wssShopMyStoreToggleBound) {
      toggle.__wssShopMyStoreToggleBound = true;
      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        if (!window.WSSShopMyStore) return;
        var current = computeState();
        if (current.state === 'disabled') {
          // No store yet — per UAT feedback, clicking the toggle here should open the
          // locator (it used to do nothing at all, since no listener was bound). Routes
          // through enable() rather than openStoreLocator() directly so it flags intent
          // to turn pickup on once a store is chosen (same pendingEnablePickup pattern
          // already used by the cart toggle's empty-store path) — this is what makes it
          // "Shop My Store" prompting a selection, as opposed to a generic "Find a
          // Store" link elsewhere, which should NOT auto-enable. WSSShopMyStore.enable()
          // itself cascades to also turn Pick Up In Store on (see the BOPIS split).
          window.WSSShopMyStore.enable({ source: source });
          return;
        }
        // Optimistic flip so the CSS track/knob transition plays immediately. The real
        // state update (and wss:shop-my-store-changed) resolves async after this;
        // in-place re-renders preserve this same button node so the animation isn't
        // cancelled by an innerHTML rebuild.
        var turningOn = !window.WSSShopMyStore.isEnabled();
        // Let the state event reconcile this optimistic visual even if persistence fails.
        root.__wssSig = null;
        setToggleVisual(toggle, turningOn, false);
        if (turningOn) {
          window.WSSShopMyStore.enable({ source: source });
        } else {
          window.WSSShopMyStore.disable({ source: source });
        }
      });
    }

    var changeBtn = root.querySelector('[data-wss-shop-my-store-change]');
    if (changeBtn && !changeBtn.__wssShopMyStoreBound) {
      changeBtn.__wssShopMyStoreBound = true;
      changeBtn.addEventListener('click', function (event) {
        event.preventDefault();
        if (typeof window.openStoreLocator === 'function') window.openStoreLocator();
      });
    }

    var selectBtn = root.querySelector('[data-wss-shop-my-store-select]');
    if (selectBtn && !selectBtn.__wssShopMyStoreBound) {
      selectBtn.__wssShopMyStoreBound = true;
      selectBtn.addEventListener('click', function (event) {
        event.preventDefault();
        // Same "Shop My Store" intent as the disabled toggle above.
        if (window.WSSShopMyStore) window.WSSShopMyStore.enable({ source: source });
      });
    }
  }

  // Mobile CTA label markup — the "on" state is a single clickable "Shopping at
  // [Location]" link (per Figma + the approved use cases), while "off"/"disabled"
  // use the "Shop My Store" title with an action sub-label: "Select a Store" when no
  // store is selected, "View Your Store" when one is selected but the toggle is off
  // (per design — this was previously missing entirely for the "off" case).
  function mobileCtaLabelHtml(state, store) {
    if (state === 'on') {
      var name = escapeHtml(store && (store.displayName || store.name) || '');
      return '<span class="wss-shop-my-store__mobile-title wss-shop-my-store__mobile-title--link">Shopping at <strong>' + name + '</strong></span>';
    }
    var sub = state === 'disabled' ? 'Select a Store' : 'View Your Store';
    return (
      '<span class="wss-shop-my-store__mobile-title">Shop My Store</span>' +
      '<span class="wss-shop-my-store__mobile-sub">' + escapeHtml(sub) + '</span>'
    );
  }

  function computeState() {
    var store = window.WSSStore ? window.WSSStore.get() : null;
    var hasStore = !!(store && (store.displayName || store.name));
    if (!hasStore) return { state: 'disabled', store: null };
    var on = window.WSSShopMyStore ? window.WSSShopMyStore.isEnabled() : false;
    return { state: on ? 'on' : 'off', store: store };
  }

  // True when we can keep the existing switch DOM and only patch body/labels — required
  // for the on/off CSS transition to play (innerHTML replacement cancels it).
  function canUpdateToggleInPlace(prevState, nextState) {
    return (prevState === 'on' || prevState === 'off') && (nextState === 'on' || nextState === 'off');
  }

  function updateMobileCta(container, state, store) {
    var mobileCta = container.querySelector('[data-wss-shop-my-store-mobile-cta]');
    if (!mobileCta) return;
    mobileCta.innerHTML =
      iconSvg('wss-shop-my-store__mobile-icon') +
      mobileCtaLabelHtml(state, store);
  }

  function bindMobileCta(container) {
    var mobileCta = container.querySelector('[data-wss-shop-my-store-mobile-cta]');
    if (!mobileCta || mobileCta.__wssShopMyStoreBound) return;
    mobileCta.__wssShopMyStoreBound = true;
    mobileCta.addEventListener('click', function (event) {
      event.preventDefault();
      var current = computeState();
      // Per spec, the "on" state's CTA ("Shopping at [Location]") opens Nosto's own
      // filter menu (where our toggle panel lives — see syncFilterPanel), not the store
      // picker; "off" still just opens the store locator to view/change the store,
      // without touching pickup mode (it's already been explicitly turned off).
      if (current.state === 'on' && openNostoFilterMenu()) return;
      if (current.state === 'disabled' && window.WSSShopMyStore) {
        // No store yet — same "Shop My Store" intent as the desktop toggle/"Select a
        // Store" (see bindStateControls), so selecting one here also turns pickup on.
        window.WSSShopMyStore.enable({ source: 'plp' });
        return;
      }
      if (typeof window.openStoreLocator === 'function') window.openStoreLocator();
    });
  }

  function renderContainer(container) {
    var computed = computeState();
    var state = computed.state;
    var store = computed.store;
    var bodyHtml = cardBodyHtml(state, store);
    var signature = JSON.stringify([state, store && store.id, bodyHtml, mobileCtaLabelHtml(state, store)]);
    if (container.__wssSig === signature) return;
    var prevState = container.getAttribute('data-wss-shop-my-store-state');
    var existingToggle = container.querySelector('[data-wss-shop-my-store-toggle]');

    // Visibility is owned by placeContainer (see EDGE case: layout shift when Nosto's
    // desktop sidebar renders after us) — content gets built here regardless, so it's
    // ready the moment placement reveals it.
    if (prevState !== state) container.setAttribute('data-wss-shop-my-store-state', state);

    // Preserve the switch node on on↔off so the track/knob transition can finish.
    if (existingToggle && canUpdateToggleInPlace(prevState, state)) {
      setToggleVisual(existingToggle, state === 'on', false);
      var body = container.querySelector('.wss-shop-my-store__body');
      if (body) body.innerHTML = bodyHtml;
      updateMobileCta(container, state, store);
      bindStateControls(container, 'plp');
      bindMobileCta(container);
      container.__wssSig = signature;
      return;
    }

    container.innerHTML =
      '<div class="wss-shop-my-store__card">' +
        '<div class="wss-shop-my-store__row">' +
          iconSvg('wss-shop-my-store__card-icon') +
          '<span class="wss-shop-my-store__title">Shop My Store</span>' +
          toggleSwitchHtml(state === 'on', state === 'disabled') +
        '</div>' +
        '<div class="wss-shop-my-store__body">' + bodyHtml + '</div>' +
      '</div>' +
      '<button type="button" class="wss-shop-my-store__mobile-cta" data-wss-shop-my-store-mobile-cta>' +
        iconSvg('wss-shop-my-store__mobile-icon') +
        mobileCtaLabelHtml(state, store) +
      '</button>';

    bindStateControls(container, 'plp');
    bindMobileCta(container);
    container.__wssSig = signature;
  }

  // Nosto's mobile filter drawer opens via a plain checkbox + label (confirmed live —
  // #toggle-mobile-sidebar checked shows .ns-sidebar-wrapper via Nosto's own CSS), not a
  // JS API — so we can open it ourselves by checking the box, same as clicking their
  // "Filter" button would. Returns false (falling back to the store locator) if that
  // checkbox isn't present, e.g. if Nosto changes this markup.
  function openNostoFilterMenu() {
    var checkbox = document.getElementById('toggle-mobile-sidebar');
    if (!checkbox) return false;
    checkbox.checked = true;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function renderAll() {
    getContainers().forEach(renderContainer);
  }

  // ----- Nosto URL filter param sync -----
  // Applies/strips the pickup-store filter param via a full navigation (the reliable
  // option — Nosto's client-side re-filter on history.replaceState alone is unconfirmed).
  function syncFilterParam() {
    var containers = getContainers();
    if (!containers.length) return; // not on a collection/search page
    var computed = computeState();
    var config = getConfig();
    var url = new URL(window.location.href);
    var currentValue = url.searchParams.get(config.facetParam);

    if (computed.state === 'on' && computed.store) {
      var desiredValue = String(computed.store.id || '');
      if (!desiredValue || currentValue === desiredValue) return;
      url.searchParams.set(config.facetParam, desiredValue);
      applyFilterUrl(url.toString());
      return;
    }

    if (currentValue != null) {
      url.searchParams.delete(config.facetParam);
      applyFilterUrl(url.toString());
    }
  }

  // Nosto's own facet checkboxes update the URL via history.pushState and re-fetch/
  // re-render off a "popstate" listener — confirmed by reading Nosto's own search-
  // template bundle (its popstate handler re-parses location.search and re-runs the
  // search). Dispatching a synthetic popstate after our own pushState routes through
  // that exact listener, so Nosto AJAX-updates the grid itself — preserving its own
  // pagination/sort/other-active-filters state — instead of a full page reload (per UAT
  // feedback: should behave like any other filter). Falls back to a real navigation if
  // nothing visibly changes shortly after, in case this ever drifts from Nosto's
  // internals (e.g. after a Nosto-side update) — better a reload than a silently stuck
  // toggle.
  function applyFilterUrl(nextUrl) {
    var grid = document.querySelector('.ns-product-grid');
    var priorHtml = grid ? grid.innerHTML : null;
    window.history.pushState(null, '', nextUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));

    window.setTimeout(function () {
      var freshGrid = document.querySelector('.ns-product-grid');
      var changed = priorHtml === null || !freshGrid || freshGrid.innerHTML !== priorHtml;
      if (!changed) window.location.assign(nextUrl);
    }, 1500);
  }

  // ----- Desktop sidebar placement -----
  // Nosto renders the real filter sidebar and the results (badges/count/grid) as flex
  // siblings (.ns-serp > .ns-sidebar-wrapper + .content-grid). Our placeholder div sits
  // as a static sibling ahead of that whole Nosto mount, so left alone it renders as its
  // own full-width row above everything — leaving a gap beside it where Figma places the
  // filter badges/count, and pushing the grid down. To match Figma, move the container
  // into the top of Nosto's sidebar column on desktop; move it back to its original spot
  // below 960px so the mobile CTA isn't nested inside a sidebar Nosto hides on mobile.
  //
  // Nosto can wholesale-rebuild the sidebar's innerHTML on filter changes, which would
  // detach (not delete) our container — so `containers` below is captured once, not
  // re-queried, and a MutationObserver re-homes any detached container every time Nosto
  // touches the DOM (debounced to one placement pass per animation frame).
  var DESKTOP_MQ = window.matchMedia('(min-width: 960px)');

  // True once Nosto has finished rendering *something* for the current filters — either
  // the normal result-count element, or its own "No results found" empty state (which,
  // confirmed live, renders NO totalResults element at all — just a different block).
  // Used both to detect a genuine zero-result state and to tell "sidebar hasn't loaded
  // yet" apart from "this result set genuinely has no sidebar."
  function hasResultsRendered() {
    return !!document.querySelector('[data-nosto-element="totalResults"]') ||
      !!document.querySelector('.ns-serp .content-grid');
  }

  // Hide the widget entirely on a zero-result filtered state (reported as a layout bug —
  // there's nothing useful for it to say next to an empty grid).
  function hasZeroResults() {
    var el = document.querySelector('[data-nosto-element="totalResults"]');
    if (el) {
      var match = (el.textContent || '').match(/\d+/);
      return !!match && parseInt(match[0], 10) === 0;
    }
    // No count element — Nosto's "No results found" empty state doesn't render one at
    // all, so absence alone doesn't mean "still loading." Only treat it as zero once
    // Nosto has actually rendered something for this page and there are no product tiles.
    if (!hasResultsRendered()) return false;
    return !document.querySelector('.ns-product-grid .ns-product');
  }

  // Nosto sometimes renders a result set with no sidebar at all (e.g. filtered down far
  // enough that no facets have options left) — confirmed live via a captured DOM snippet
  // with several filters applied. Rather than leave the widget with nowhere to go, build
  // a stand-in sidebar column reusing Nosto's own classes (so it sizes identically) and
  // place the widget there instead. Marked so we always prefer a real sidebar over ours
  // once Nosto renders one again (e.g. a filter gets removed and facets return).
  var SYNTHETIC_SIDEBAR_ATTR = 'data-wss-synthetic-sidebar';

  function findRealSidebarContent() {
    return document.querySelector('.ns-sidebar-wrapper:not([' + SYNTHETIC_SIDEBAR_ATTR + ']) .ns-sidebar-content');
  }

  function ensureSyntheticSidebarContent() {
    var serp = document.querySelector('.ns-serp');
    var contentGrid = serp && serp.querySelector('.content-grid');
    if (!serp || !contentGrid) return null;
    var wrapper = serp.querySelector('[' + SYNTHETIC_SIDEBAR_ATTR + ']');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.setAttribute(SYNTHETIC_SIDEBAR_ATTR, '');
      wrapper.className = 'ns-sidebar-wrapper ns-d-none ns-d-md-block ns-h-100 ns-col-3 ns-col-l-2';
      wrapper.appendChild(document.createElement('div'))
        .className = 'ns-sidebar-content ns-relative ns-h-100 ns-mr-0 ns-mr-md-auto';
    }
    if (wrapper.parentNode !== serp || wrapper.nextSibling !== contentGrid) {
      serp.insertBefore(wrapper, contentGrid);
    }
    return wrapper.querySelector('.ns-sidebar-content');
  }

  function removeSyntheticSidebar() {
    var wrapper = document.querySelector('[' + SYNTHETIC_SIDEBAR_ATTR + ']');
    if (wrapper && wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
  }

  // On desktop, Nosto's sidebar doesn't exist yet at first paint — placing the container
  // in its fallback spot and showing it immediately produces a visible flash-then-jump
  // once the real sidebar renders and we re-home it (EDGE case: layout shift on load/
  // toggle). So on desktop we keep it hidden until it's actually inside the sidebar.
  // FALLBACK_MS bounds how long we'll wait — if Nosto's sidebar never shows up (it fails
  // to load, or this page genuinely has none), reveal in the fallback spot anyway rather
  // than hiding the widget forever.
  var FALLBACK_MS = 4000;
  var placementStartedAt = null;

  function placeContainer(container) {
    if (hasZeroResults()) {
      container.hidden = true;
      removeSyntheticSidebar();
      return;
    }

    if (!container.__wssAnchor) {
      var anchor = document.createComment('wss-shop-my-store-anchor');
      container.parentNode.insertBefore(anchor, container);
      container.__wssAnchor = anchor;
    }
    var anchor = container.__wssAnchor;
    var sidebarContent = findRealSidebarContent();

    if (DESKTOP_MQ.matches && sidebarContent) {
      removeSyntheticSidebar(); // a real sidebar exists now — drop our stand-in, if any
      if (container.parentNode !== sidebarContent || sidebarContent.firstChild !== container) {
        sidebarContent.insertBefore(container, sidebarContent.firstChild);
      }
      container.hidden = false;
      return;
    }

    if (DESKTOP_MQ.matches && hasResultsRendered()) {
      // Nosto has finished rendering this state with no sidebar of its own — use our
      // stand-in column instead of falling back to the anchor spot (which orphaned the
      // widget as a full-width row above the grid — EDGE case: layout gap with several
      // filters applied).
      var synthetic = ensureSyntheticSidebarContent();
      if (synthetic) {
        if (container.parentNode !== synthetic || synthetic.firstChild !== container) {
          synthetic.insertBefore(container, synthetic.firstChild);
        }
        container.hidden = false;
        return;
      }
    }

    if (anchor.parentNode && container.parentNode !== anchor.parentNode) {
      anchor.parentNode.insertBefore(container, anchor.nextSibling);
    }

    if (!DESKTOP_MQ.matches) {
      // Mobile's fallback spot is its permanent home — nothing to wait for.
      container.hidden = false;
      return;
    }

    // Desktop, still loading (neither sidebar, results, nor a place for a stand-in
    // sidebar yet) — reveal in the fallback spot after FALLBACK_MS as a last resort, in
    // case Nosto never finishes rendering at all.
    if (placementStartedAt !== null && Date.now() - placementStartedAt > FALLBACK_MS) {
      container.hidden = false;
    } else {
      container.hidden = true;
    }
  }

  // ----- Mobile filter-flyout panel -----
  // Per the approved use cases, mobile needs the toggle both as the always-visible CTA
  // above the grid (handled by placeContainer's "off-desktop" branch) AND inside Nosto's
  // own filter flyout — confirmed to be the exact same `.ns-sidebar-content` Nosto shows
  // as the mobile drawer via its "Filter" button (not a separate mobile-only element).
  // Since the primary container already lives above the grid on mobile, this injects a
  // second, independent panel node into that shared sidebar content instead of relocating
  // the primary container there (which would remove the always-visible CTA). On desktop
  // the primary container itself already lives in `.ns-sidebar-content` via placeContainer,
  // so this panel is removed there to avoid showing the widget twice.
  var FILTER_PANEL_ATTR = 'data-wss-shop-my-store-filter-panel';

  function renderFilterPanel(panel) {
    var computed = computeState();
    var state = computed.state;
    var store = computed.store;
    var bodyHtml = filterPanelBodyHtml(state, store);
    var signature = JSON.stringify([state, store && store.id, bodyHtml]);
    if (panel.__wssSig === signature) return;
    var prevState = panel.getAttribute('data-wss-shop-my-store-state');
    var existingToggle = panel.querySelector('[data-wss-shop-my-store-toggle]');
    if (prevState !== state) panel.setAttribute('data-wss-shop-my-store-state', state);

    // Same in-place path as renderContainer — keep the switch DOM for the on/off animation.
    if (existingToggle && canUpdateToggleInPlace(prevState, state)) {
      setToggleVisual(existingToggle, state === 'on', false);
      var body = panel.querySelector('.wss-shop-my-store__body');
      if (body) body.innerHTML = bodyHtml;
      bindStateControls(panel, 'plp-filter-flyout');
      panel.__wssSig = signature;
      return;
    }

    // Gray section (panel) containing an inset white card — matches the Figma mobile
    // filter-flyout mockup (measured: full-bleed #F9F9F9 band, ~32px inset white card).
    panel.innerHTML =
      '<div class="wss-shop-my-store__filter-panel-card">' +
        '<div class="wss-shop-my-store__row">' +
          iconSvg('wss-shop-my-store__card-icon') +
          '<span class="wss-shop-my-store__title">Shop My Store</span>' +
          toggleSwitchHtml(state === 'on', state === 'disabled') +
        '</div>' +
        '<div class="wss-shop-my-store__body">' + bodyHtml + '</div>' +
      '</div>';
    bindStateControls(panel, 'plp-filter-flyout');
    panel.__wssSig = signature;
  }

  function syncFilterPanel() {
    // Only Nosto's real mobile drawer, never our desktop-only stand-in sidebar.
    var sidebarContent = findRealSidebarContent();
    if (!sidebarContent) return;

    if (hasZeroResults()) {
      var zeroResultsExisting = sidebarContent.querySelector('[' + FILTER_PANEL_ATTR + ']');
      if (zeroResultsExisting && zeroResultsExisting.parentNode) zeroResultsExisting.parentNode.removeChild(zeroResultsExisting);
      return;
    }

    if (DESKTOP_MQ.matches) {
      var existing = sidebarContent.querySelector('[' + FILTER_PANEL_ATTR + ']');
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      return;
    }

    var panel = sidebarContent.querySelector('[' + FILTER_PANEL_ATTR + ']');
    if (!panel) {
      panel = document.createElement('div');
      panel.setAttribute(FILTER_PANEL_ATTR, '');
      panel.className = 'wss-shop-my-store__filter-panel';
    }
    // Below the "Filters" header bar, above the GENDER/BRAND/... list — not above the header.
    var header = sidebarContent.querySelector('.ns-sidebar-header');
    var targetNextSibling = header ? header.nextSibling : sidebarContent.firstChild;
    if (panel.previousSibling !== header || panel.parentNode !== sidebarContent) {
      sidebarContent.insertBefore(panel, targetNextSibling);
    }
    renderFilterPanel(panel);
  }

  // ----- Hide the raw applied-filter chip for our own facet -----
  // Nosto has no friendly label configured for filter.extra.custom-pickup_store_ids, so
  // its applied-filters bar shows the raw "extra.custom-pickup_store_ids: <id>" chip
  // verbatim — confirmed live via a captured DOM snippet, which is also where this exact
  // selector (Nosto's own ".ns-selected-filter" per-chip wrapper) comes from. We already
  // surface this filter through our own widget ("Shopping at [Store]" + toggle), so hide
  // just this one chip and leave any other active filters' chips (and "Clear Filters")
  // untouched.
  function facetChipKey() {
    return getConfig().facetParam.replace(/^filter\./, '');
  }

  function hideRawFacetChip() {
    var key = facetChipKey();
    if (!key) return;
    document.querySelectorAll('.ns-selected-filter').forEach(function (chip) {
      if (chip.hasAttribute('data-wss-hidden-facet-chip')) return;
      if ((chip.textContent || '').trim().indexOf(key) !== 0) return;
      // Nosto's own ".ns-d-flex { display: flex !important; }" (present on this chip)
      // beats a plain inline style, so this has to be !important too or the chip stays
      // visible despite being "hidden" (confirmed live — the attribute/style were being
      // set correctly but had no visual effect until this).
      chip.style.setProperty('display', 'none', 'important');
      chip.setAttribute('data-wss-hidden-facet-chip', '');
    });
  }

  // ----- Product count message -----
  // Nosto's own count text ("214 products") has no location-aware copy, and there's no
  // template hook to inject into — but the element itself has a stable selector
  // (confirmed live), so rewrite its text directly instead, same approach as the chip
  // above. Always re-derive the leading number from the CURRENT text rather than tracking
  // state, so this is safe to re-run on every Nosto re-render (including over our own
  // previous rewrite — "1 Products Available at X" still starts with "1").
  function updateResultsCountMessage() {
    var el = document.querySelector('[data-nosto-element="totalResults"]');
    if (!el) return;
    var match = (el.textContent || '').match(/\d+/);
    if (!match) return;
    var count = match[0];
    var computed = computeState();
    if (computed.state === 'on' && computed.store) {
      // Built as nodes (not innerHTML) since `count` is read back from the DOM.
      var name = computed.store.displayName || computed.store.name || '';
      var strongText = count + ' Products Available';
      var targetText = strongText + ' at ' + name;
      var existingStrong = el.querySelector('strong');
      if (el.textContent === targetText && existingStrong && existingStrong.textContent === strongText) return;
      var strong = document.createElement('strong');
      strong.textContent = strongText;
      el.replaceChildren(strong, document.createTextNode(' at ' + name));
    } else if (el.querySelector('strong')) {
      // Toggled off without a reload (e.g. removing the chip directly) — Nosto owns this
      // text and will fully overwrite it on its own next re-render anyway, but restore its
      // plain style now so nothing looks stuck mid-transition.
      var plainText = count + ' products';
      if (el.textContent !== plainText) el.textContent = plainText;
    }
  }

  function initSidebarPlacement(containers) {
    placementStartedAt = Date.now();
    var scheduled = false;
    function placeAll() {
      scheduled = false;
      // Placement writes must not schedule another pass through our own observer.
      observer.disconnect();
      try {
        containers.forEach(placeContainer);
        syncFilterPanel();
        hideRawFacetChip();
        updateResultsCountMessage();
      } finally {
        observer.observe(document.body, { childList: true, subtree: true });
      }
    }

    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(placeAll);
    });
    placeAll();

    // Safety net for the fallback-reveal timeout above — if Nosto's sidebar never shows
    // up, nothing will trigger a mutation to re-run placeAll() past FALLBACK_MS on its own.
    window.setTimeout(placeAll, FALLBACK_MS + 100);

    if (DESKTOP_MQ.addEventListener) {
      DESKTOP_MQ.addEventListener('change', placeAll);
    } else if (DESKTOP_MQ.addListener) {
      DESKTOP_MQ.addListener(placeAll);
    }
  }

  // syncFilterPanel() only re-runs on its own (debounced) placement pass, which is driven
  // by DOM mutations — not by state-change events. Call it alongside renderAll() anywhere
  // state can change so the flyout panel's content doesn't go stale between mutations.
  function renderEverything() {
    renderAll();
    syncFilterPanel();
  }

  // ----- External filter-chip removal sync -----
  // Turning the toggle ON/OFF is the only thing that changes WSSShopMyStore's state, but
  // Nosto's own "applied filters" chip lets a customer remove our facet param directly —
  // via history.pushState/replaceState, not a page reload, so init() never re-runs to
  // notice. Without this, the toggle (and flyout tile, since they share the same state)
  // keeps showing "on" after the chip — and the filter itself — are already gone. This
  // only turns Shop My Store off, not Pick Up In Store — removing the PLP filter chip
  // isn't a cart action, so per the BOPIS split it shouldn't cascade to the cart toggle.
  function currentUrlHasFacetParam() {
    return new URL(window.location.href).searchParams.get(getConfig().facetParam) != null;
  }

  function watchForExternalFilterRemoval() {
    var hadParam = currentUrlHasFacetParam();
    function check() {
      var hasParam = currentUrlHasFacetParam();
      if (hadParam && !hasParam && window.WSSShopMyStore && window.WSSShopMyStore.isEnabled()) {
        window.WSSShopMyStore.disable({ source: 'nosto-filter-removed' });
      }
      hadParam = hasParam;
    }
    ['pushState', 'replaceState'].forEach(function (method) {
      var original = history[method];
      if (typeof original !== 'function' || original.__wssPatched) return;
      var patched = function () {
        var result = original.apply(this, arguments);
        check();
        return result;
      };
      patched.__wssPatched = true;
      history[method] = patched;
    });
    window.addEventListener('popstate', check);
  }

  function init() {
    var containers = getContainers();
    if (!containers.length) return;
    renderAll();
    initSidebarPlacement(containers);
    watchForExternalFilterRemoval();

    // Unlike WSSPickupMode (backed by the async cart fulfillment_method attribute),
    // WSSShopMyStore is backed by plain localStorage, so isEnabled() is correct
    // synchronously on first paint — no need to wait on a ready promise the way the old
    // pickup-mode-based version of this widget did before the BOPIS split. renderAll()
    // above already rendered off that same synchronous state, so only the URL param sync
    // is left to do here.
    syncFilterParam();

    window.addEventListener('wss:store-changed', function () {
      renderEverything();
      syncFilterParam();
    });
    window.addEventListener('wss:shop-my-store-changed', function () {
      renderEverything();
      syncFilterParam();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
